<template>
    <div class="space-y-3">
        <h2 class="font-semibold text-lg" @dblclick="showAdvanced = true">{{ t('settings.title') }}</h2>

        <AppButton v-if="showAdvanced" variant="success" block @click="showStats">{{
            t('settings.showStatistics')
        }}</AppButton>

        <div class="bg-surface-card rounded p-3">
            <AppCheckbox v-model="settings.sound" :label="t('settings.sound')" />
            <div class="text-sm opacity-70 ml-7">{{ t('settings.soundSub') }}</div>
        </div>

        <div class="bg-surface-card rounded p-3 space-y-2">
            <div class="text-sm opacity-70">{{ t('settings.camera') }}</div>
            <AppSelect :model-value="settings.camera" :items="cameras" @update:modelValue="onCameraChange" />
        </div>

        <div v-if="showAdvanced" class="bg-surface-card rounded p-3">
            <div class="text-sm opacity-70 mb-2">{{ t('settings.nfc_heading') }}</div>
            <AppCheckbox v-model="settings.nfc" :label="t('settings.nfc')" />
        </div>

        <div v-if="showAdvanced" class="bg-surface-card rounded p-3">
            <AppCheckbox v-model="settings.showTicketCount" :label="t('settings.showTicketCount')" />
        </div>

        <div class="bg-surface-card rounded p-3 space-y-2">
            <div class="text-sm opacity-70">{{ t('settings.groupDisplay') }}</div>
            <AppSelect :model-value="settings.group" :items="groupItems" @update:modelValue="onGroupChange" />
        </div>

        <div class="bg-surface-card rounded p-3 space-y-2">
            <div class="text-sm opacity-70">{{ t('settings.olderEvents') }}</div>
            <AppSelect :model-value="settings.olderEvents" :items="olderItems" @update:modelValue="onOlderChange" />
        </div>

        <div class="bg-surface-card rounded p-3 space-y-2">
            <div class="text-sm opacity-70">{{ t('settings.language') }}</div>
            <AppSelect :model-value="settings.lang" :items="languageItems" @update:modelValue="onLangChange" />
        </div>

        <div v-if="showAdvanced" class="bg-surface-card rounded p-3 space-y-2">
            <div class="text-sm opacity-70">{{ t('settings.qTitle') }}</div>
            <div class="text-xs opacity-50">{{ t('settings.qPatchSize') }}</div>
            <AppSelect
                :model-value="settings.patchSize"
                :items="patchSizeItems"
                @update:modelValue="onPatchSizeChange"
            />
            <div class="text-xs opacity-50">{{ t('settings.qFrequency') }}</div>
            <AppSelect
                :model-value="settings.frequency"
                :items="frequencyItems"
                @update:modelValue="onFrequencyChange"
            />
        </div>

        <div class="mt-6">
            <h3 class="font-semibold">
                {{ t('settings.queueTitle') }} ({{ t('settings.queueSending') }}:
                <span v-if="queue.sending">{{ t('settings.queueYes') }}</span>
                <span v-else>{{ t('settings.queueNo') }}</span
                >)
            </h3>
            <div v-if="queue.sendQueue.length" class="text-sm">
                <span v-for="row in queue.sendQueue" :key="row.code">{{ row.code }}, </span>
            </div>
            <div v-else class="opacity-70 text-sm">{{ t('settings.queueEmpty') }}</div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { computed, onMounted, ref } from 'vue'
    import { useRouter } from 'vue-router'
    import { useSettingsStore, type GroupDisplay, type SettingsLang } from '@/stores/settings'
    import { useQueueStore } from '@/stores/queue'
    import { useI18n } from 'vue-i18n'
    import { listCameras } from '@/services/scanner/detector'
    import { setLocale, SUPPORTED_LOCALES, type Locale } from '@/i18n'
    import AppCheckbox from '@/components/ui/AppCheckbox.vue'
    import AppSelect from '@/components/ui/AppSelect.vue'
    import AppButton from '@/components/ui/AppButton.vue'

    const settings = useSettingsStore()
    const queue = useQueueStore()
    const router = useRouter()
    const { t } = useI18n()

    const cameras = ref<{ label: string; value: string }[]>([])
    const showAdvanced = ref(false)

    const groupItems = computed(() => [
        { label: t('settings.groupDisplayNone'), value: '' },
        { label: t('settings.groupDisplayEvent'), value: 'event' },
        { label: t('settings.groupDisplayTicketset'), value: 'ticketset' },
        { label: t('settings.groupDisplayPrice'), value: 'price' },
        { label: t('settings.groupDisplayTicketsetPrice'), value: 'ticketset_price' }
    ])

    const langKey: Record<Locale, string> = {
        cs: 'settings.langCs',
        de: 'settings.langDe',
        en: 'settings.langEn',
        hr: 'settings.langHr',
        hu: 'settings.langHu',
        pl: 'settings.langPl',
        sk: 'settings.langSk'
    }

    const languageItems = computed(() => SUPPORTED_LOCALES.map(loc => ({ label: t(langKey[loc]), value: loc })))

    const olderItems = computed(() => [
        { label: t('settings.olderEventsWeek'), value: 7 },
        { label: t('settings.olderEventsMonth'), value: 30 },
        { label: t('settings.olderEvents3Month'), value: 90 },
        { label: t('settings.olderEvents6Month'), value: 180 },
        { label: t('settings.olderEventsYear'), value: 365 },
        { label: t('settings.olderEventsAll'), value: 9999 }
    ])

    const patchSizeItems = [20, 30, 40, 50, 60, 70, 80, 90, 100].map(v => ({ label: String(v), value: v }))
    const frequencyItems = Array.from({ length: 15 }, (_, i) => i + 1).map(v => ({ label: String(v), value: v }))

    onMounted(async () => {
        try {
            const devs = await listCameras()
            cameras.value = [
                { value: '', label: '-- Autodetect --' },
                { value: '-', label: '-- Vypnout čtečku --' },
                ...devs.map(d => ({ value: d.deviceId, label: d.label || d.deviceId }))
            ]
        } catch {
            cameras.value = [{ value: '', label: '-- Autodetect --' }]
        }
    })

    function showStats() {
        void router.push({ name: 'statistics' })
    }

    function onLangChange(value: string | number) {
        settings.lang = String(value) as SettingsLang
        setLocale(settings.lang)
    }

    function onGroupChange(value: string | number) {
        settings.group = value as GroupDisplay
    }

    function onCameraChange(value: string | number) {
        settings.camera = String(value)
    }

    function onPatchSizeChange(value: string | number) {
        settings.patchSize = Number(value)
    }

    function onFrequencyChange(value: string | number) {
        settings.frequency = Number(value)
    }

    function onOlderChange(value: string | number) {
        settings.olderEvents = Number(value)
    }
</script>
